// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { identity, constant } from 'lodash';

// Utils
import dontUpdateForKeys from '../../../utils/milanoteRecompose/dontUpdateForKeys';
import {
    getCaption,
    getElementId,
    getShowCaption,
    getShowTitle,
} from '../../../../common/elements/utils/elementPropertyUtils';

// Components
import newElementDecorator from '../../newElementDecorator';
import elementLineEdgeDropTarget from '../../line/elementLineEdgeDropTarget';
import captionContainerDecorator from '../../../components/caption/captionContainerDecorator';
import Table from './Table';
import groupResizeBehaviourDecorator from '../../resizing/groupResizeBehaviourDecorator';
import tableResizeDecorator from './tableResizeDecorator';

// Actions
import { updateElement, createAndEditElement } from '../../actions/elementActions';
import { moveElementsToTrash } from '../../actions/elementShortcutActions';
import { deselectAllElements } from '../../selection/selectionActions';

// Constants
import { TABLE_MIN_HEIGHT, TABLE_MIN_WIDTH } from '../../../../common/table/tableConstants';
import { SHOW_TITLE_PROPERTY } from '../../../../common/taskLists/taskListConstants';
import { ELEMENT_DEFAULT_WIDTH } from '../../../../common/elements/elementConstants';
import { ElementType } from '../../../../common/elements/elementTypes';
import groupResizeStateDecorator from '../../resizing/groupResizeStateDecorator';
import { MaintainAspectRatioMode } from '../../resizing/resizeTypes';

const mapDispatchToProps = (dispatch) => ({
    dispatchCreateAndEditNewElement: ({ location, currentBoardId, element, creationSource, content }) => {
        const caption = getCaption(element);

        return dispatch(
            createAndEditElement({
                creationSource,
                elementType: ElementType.TABLE_TYPE,
                location,
                currentBoardId,
                content: {
                    caption: caption && caption.toJS(),
                    showCaption: getShowCaption(element),
                    showTitle: getShowTitle(element),
                    ...content,

                    // For table elements, always set the width to the default width
                    width: ELEMENT_DEFAULT_WIDTH,
                },
                select: true,
            }),
        );
    },
    dispatchMoveElementsToTrash: ({ currentBoardId, elementIds }) =>
        dispatch(moveElementsToTrash({ currentBoardId, elementIds })),
    dispatchUpdateTitle: ({ id, title, transactionId }) =>
        dispatch(updateElement({ id, changes: { title }, transactionId })),
    dispatchHideTitle: ({ id, transactionId }) =>
        dispatch(updateElement({ id, changes: { [SHOW_TITLE_PROPERTY]: false }, transactionId })),
    dispatchDeselectAllElements: () => dispatch(deselectAllElements()),
});

const getIsRepositionOnly = ({ isMultiResizingOrAnimating }) => isMultiResizingOrAnimating; // Tables resize differently to other elements, so we only allow resizing by itself

@connect(null, mapDispatchToProps)
@elementLineEdgeDropTarget
@dontUpdateForKeys(['dragModifierKeys'])
@captionContainerDecorator
@groupResizeStateDecorator(constant(MaintainAspectRatioMode.NEVER), TABLE_MIN_WIDTH, TABLE_MIN_HEIGHT)
@tableResizeDecorator
@groupResizeBehaviourDecorator(getIsRepositionOnly)
@newElementDecorator
class TableContainer extends React.Component {
    moveElementsToTrash = () => {
        const { currentBoardId, element, dispatchMoveElementsToTrash } = this.props;
        dispatchMoveElementsToTrash({ currentBoardId, elementIds: [getElementId(element)] });
    };

    shouldComponentUpdate(nextProps) {
        return !nextProps.isElementOpenInModal || !this.props.isElementOpenInModal;
    }

    submitTableTitle = () => {
        const { dispatchDeselectAllElements } = this.props;
        dispatchDeselectAllElements();
    };

    render() {
        const { connectLineEdgeDropTarget, canConnectLineEdge, createNewElement } = this.props;

        const lineEdgeConnector =
            connectLineEdgeDropTarget && canConnectLineEdge ? connectLineEdgeDropTarget : identity;

        return lineEdgeConnector(
            <div>
                <Table
                    {...this.props}
                    updateElementType={this.updateElementType}
                    saveContent={this.saveContent}
                    createNewElement={createNewElement}
                    submitTitle={this.submitTableTitle}
                    moveElementsToTrash={this.moveElementsToTrash}
                />
            </div>,
        );
    }
}

TableContainer.propTypes = {
    element: PropTypes.object.isRequired,
    elementId: PropTypes.string,
    currentBoardId: PropTypes.string.isRequired,
    languagePreference: PropTypes.string,
    createNewElement: PropTypes.func,
    inList: PropTypes.string,
    canConnectLineEdge: PropTypes.bool,
    gridSize: PropTypes.number,
    isElementOpenInModal: PropTypes.bool,
    dispatchSaveTextContent: PropTypes.func,
    dispatchMoveElementsToTrash: PropTypes.func,
    isEditable: PropTypes.bool,
    connectLineEdgeDropTarget: PropTypes.func,
    dispatchStartEditingTitle: PropTypes.func,
    dispatchUpdateElementType: PropTypes.func,
    dispatchDeselectAllElements: PropTypes.func,
};

export default TableContainer;
